import { useState, useRef } from 'react';
import { ReactComponent as LinkIcon } from '../../assets/link.svg';
import { ReactComponent as NewsIcon } from './assets/news.svg';
import Tooltip from '../Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '../../utils/useClickOutside';
import styles from './WhatsNew.module.scss';

interface WhatsNewProps {
  hasNewVersion: boolean;
  currentVersion?: string | null;
  lastSeenVersion: string | null;
  onDismiss: () => void;
  onApply: () => void;
}

export function WhatsNew({
  hasNewVersion,
  currentVersion,
  lastSeenVersion,
  onDismiss,
  onApply,
}: WhatsNewProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useClickOutside(modalRef, () => {
    if (isModalOpen) {
      handleClose();
    }
  });

  const handleClick = () => {
    if (isModalOpen) {
      handleClose();
    } else {
      setIsModalOpen(true);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    onDismiss();
  };

  const handleRefresh = () => {
    // Apply the new version (update localStorage) before refreshing
    onApply();
    window.location.reload();
  };

  return (
    <div className={styles.whatsNewContainer}>
      <button
        type="button"
        className={styles.whatsNewButton}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleClick}
      >
        <Tooltip content={t("What's new?")} position="right">
          <div className={styles.iconWrapper}>
            <NewsIcon />
            {hasNewVersion && <div className={styles.badge} />}
          </div>
        </Tooltip>
        <span className={styles.itemText}>{t("What's new?")}</span>
      </button>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} ref={modalRef}>
            <div className={styles.modalContent}>
              <p className={styles.description}>
                {hasNewVersion || (currentVersion !== lastSeenVersion)
                  ? t('A new version of Osavul platform is now available')
                  : t('No recent updates')}
              </p>
              {(hasNewVersion || (currentVersion !== lastSeenVersion)) && (
                <button
                  className={styles.updateButton}
                  onClick={() => handleRefresh()}
                >
                  {t('Update')}
                </button>
              )}
            </div>
            <div className={styles.modalFooter}>
              <a
                className={styles.infoButton}
                target='_blank'
                rel='noreferrer'
                href={`https://docs.osavul.cloud/Osavul-Release-Notes-2f1d4318dace80eb9db4dc4f8950236c`}
              >
                {t('See all releases')}
                <div className={styles.linkIcon}>
                  <LinkIcon />
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
